// The Cases list's state lives in the address, so back, reload and a link
// from Home or a colleague open the same list (CMS review rania-07,
// rania-11, nadia-03): view, q, page, paid, tests, sort, dir and the filters
// below. This file reads and writes them, builds the API body and the
// links, and says in words what a filtered count covers (nadia-17).

// The filters beside the tabs, as they are written in the address.
//   from, to   submitted between two days (YYYY-MM-DD), both included
//   status     one status          country, visa   destination, visa type ids
//   owner      a staff id, or "none"
//   waiting    3 or 7: open cases waiting that many days or more
//   docs       to_review | new_copies | waiting_customer
//   due        past | week: open cases past the day promised, or due
//              within 7 days (CMS review round 2, rania-10)
export const FILTER_KEYS = ["from", "to", "status", "country", "visa", "owner", "waiting", "docs", "due"];

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const STATUSES = ["incomplete", "pending", "under_review", "approved", "rejected", "cancelled", "expired"];
const DOCS = ["to_review", "new_copies", "waiting_customer"];
const DUE = ["past", "week"];
const positive = (value) => (/^\d+$/.test(String(value ?? "")) && Number(value) > 0 ? Number(value) : null);

// The filters in an address (URLSearchParams), each checked.
export function readFilters(params) {
  const get = (key) => params.get(key);
  const owner = get("owner") === "none" ? "none" : positive(get("owner"));
  return {
    from: DAY.test(get("from") || "") ? get("from") : null,
    to: DAY.test(get("to") || "") ? get("to") : null,
    status: STATUSES.includes(get("status")) ? get("status") : null,
    country: positive(get("country")),
    visa: positive(get("visa")),
    owner,
    waiting: ["3", "7"].includes(get("waiting")) ? Number(get("waiting")) : null,
    docs: DOCS.includes(get("docs")) ? get("docs") : null,
    due: DUE.includes(get("due")) ? get("due") : null,
  };
}

export const activeFilterCount = (filters) => FILTER_KEYS.filter((key) => key !== "to" && filters[key] != null).length + (filters.to && !filters.from ? 1 : 0);

// The filters as the API's cases/list body takes them.
export function filterBody(filters) {
  const body = {};
  if (filters.from) body.from = filters.from;
  if (filters.to) body.to = filters.to;
  if (filters.status) body.status = filters.status;
  if (filters.country) body.country_id = filters.country;
  if (filters.visa) body.visa_type_id = filters.visa;
  if (filters.owner) body.owner = filters.owner;
  if (filters.waiting) body.waiting = filters.waiting;
  if (filters.docs) body.docs = filters.docs;
  if (filters.due) body.due = filters.due;
  return body;
}

// The orders the list offers, and each view's own (waiting work oldest
// first; drafts, done and all newest first). A column header sorts by that
// column, both ways.
export const SORTS = [
  { key: "paid_first", label: "Paid first" },
  { key: "oldest", label: "Oldest first" },
  { key: "newest", label: "Newest first" },
  { key: "trip", label: "Trip soonest" },
  // The day promised to the customer, soonest first (rania-10).
  { key: "due", label: "Due soonest" },
];
export const COLUMN_SORTS = {
  ref: { label: "Case number", asc: "lowest first", desc: "highest first" },
  customer: { label: "Customer", asc: "A to Z", desc: "Z to A" },
  visa: { label: "Destination", asc: "A to Z", desc: "Z to A" },
  status: { label: "Status", asc: "A to Z", desc: "Z to A" },
  waiting: { label: "Waiting", asc: "longest first", desc: "shortest first" },
  trip: { label: "Trip", asc: "soonest first", desc: "latest first" },
  owner: { label: "Owner", asc: "A to Z", desc: "Z to A" },
  total: { label: "Total", asc: "lowest first", desc: "highest first" },
};
export const defaultSort = (view) =>
  view === "needs_action" || view === "visa_to_send" ? "paid_first" : ["drafts", "done", "all"].includes(view) ? "newest" : "oldest";

// "Sorted by Owner, A to Z", for the Order menu when a header sorts.
export const columnSortLabel = (sort, dir) => (COLUMN_SORTS[sort] ? `${COLUMN_SORTS[sort].label}, ${COLUMN_SORTS[sort][dir === "desc" ? "desc" : "asc"]}` : null);

// A link to the Cases list with these settings. Keys as in the address;
// empty values are left out. tests: true shows test accounts.
export function casesLink(settings = {}) {
  const params = new URLSearchParams();
  const order = ["view", "paid", "tests", "from", "to", "status", "country", "visa", "owner", "waiting", "docs", "due", "q"];
  for (const key of order) {
    const value = settings[key];
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (key === "view" && value === "needs_action") continue;
    if (key === "paid" && value === "all") continue;
    params.set(key, key === "tests" ? (value === true || value === "1" ? "1" : "0") : String(value));
  }
  const query = params.toString();
  return query ? `/cases?${query}` : "/cases";
}

const pad = (n) => String(n).padStart(2, "0");
export const dayString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// The submitted-date choices, as days on this computer's calendar.
export const PERIODS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_year", label: "This year" },
];

export function periodDays(key, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const shift = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };
  switch (key) {
    case "today":
      return { from: dayString(today), to: dayString(today) };
    case "this_week":
      return { from: dayString(monday), to: dayString(shift(monday, 6)) };
    case "last_week":
      return { from: dayString(shift(monday, -7)), to: dayString(shift(monday, -1)) };
    case "this_month":
      return { from: dayString(new Date(today.getFullYear(), today.getMonth(), 1)), to: dayString(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    case "last_month":
      return { from: dayString(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: dayString(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case "this_year":
      return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
    default:
      return { from: null, to: null };
  }
}

// Which named period two days are, or null ("Choose dates").
export function periodOf(from, to, now = new Date()) {
  if (!from && !to) return "any";
  const found = PERIODS.find((p) => {
    const days = periodDays(p.key, now);
    return days.from === from && days.to === to;
  });
  return found ? found.key : null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-10-05" -> "5 Oct 2026".
export function dayWords(day) {
  if (!day || !DAY.test(day)) return "";
  const [y, m, d] = day.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

// "submitted 1 Oct 2026 – 31 Oct 2026", "submitted this week (27 Oct – 2 Nov 2026)".
export function datesWords(from, to, now = new Date()) {
  if (!from && !to) return "";
  const named = periodOf(from, to, now);
  const span = from && to ? (from === to ? dayWords(from) : `${dayWords(from)} – ${dayWords(to)}`) : from ? `from ${dayWords(from)}` : `until ${dayWords(to)}`;
  const label = named && named !== "any" ? PERIODS.find((p) => p.key === named).label.toLowerCase() : null;
  return label ? `submitted ${label} (${span})` : `submitted ${span}`;
}

export const STATUS_WORDS = {
  incomplete: "Draft",
  pending: "Submitted",
  under_review: "In review",
  approved: "Approved",
  rejected: "Refused",
  cancelled: "Cancelled",
  expired: "Expired",
};
const DOCS_WORDS = { to_review: "Documents to review", new_copies: "New copies asked for", waiting_customer: "Waiting on the customer" };
const DUE_WORDS = { past: "Past the promised day", week: "Due within 7 days" };

// Each active filter as a removable chip: [{ key, label, clear: [keys] }].
// names: { destinations, visaTypes, owners } from the API, for the labels.
export function filterChips(filters, { paid = "all", names = {}, me } = {}, now = new Date()) {
  const chips = [];
  const nameIn = (list, id) => (list || []).find((item) => Number(item.id) === Number(id))?.name;
  if (paid === "paid") chips.push({ key: "paid", label: "Paid", clear: ["paid"] });
  if (paid === "unpaid") chips.push({ key: "paid", label: "Not paid yet", clear: ["paid"] });
  if (paid === "pay_later") chips.push({ key: "paid", label: "Waiting for payment (Pay later)", clear: ["paid"] });
  if (filters.from || filters.to) {
    const words = datesWords(filters.from, filters.to, now);
    chips.push({ key: "dates", label: words.charAt(0).toUpperCase() + words.slice(1), clear: ["from", "to"] });
  }
  if (filters.status) chips.push({ key: "status", label: `Status: ${STATUS_WORDS[filters.status]}`, clear: ["status"] });
  if (filters.country) chips.push({ key: "country", label: nameIn(names.destinations, filters.country) || `Destination #${filters.country}`, clear: ["country"] });
  if (filters.visa) chips.push({ key: "visa", label: nameIn(names.visaTypes, filters.visa) || `Visa type #${filters.visa}`, clear: ["visa"] });
  if (filters.owner === "none") chips.push({ key: "owner", label: "Owner: nobody", clear: ["owner"] });
  else if (filters.owner) {
    const name = Number(filters.owner) === Number(me) ? "me" : nameIn(names.owners, filters.owner) || `#${filters.owner}`;
    chips.push({ key: "owner", label: `Owner: ${name}`, clear: ["owner"] });
  }
  if (filters.waiting) chips.push({ key: "waiting", label: `Waiting ${filters.waiting}+ days`, clear: ["waiting"] });
  if (filters.docs) chips.push({ key: "docs", label: DOCS_WORDS[filters.docs], clear: ["docs"] });
  if (filters.due) chips.push({ key: "due", label: DUE_WORDS[filters.due], clear: ["due"] });
  return chips;
}

// An empty list under the due filter, in words that help (round 3,
// rania-18): what is not there, and the next useful list, the late cases
// when there are some, else those waiting 7+ days. stats: the list's own
// (they don't follow the due filter). others: other filters are on too.
// { text, next: { label, changes } | null, clear: { label, changes } }
export function dueEmpty(due, stats = {}, { others = false } = {}) {
  if (!DUE.includes(due)) return null;
  const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  const cases = (count) => (count === 1 ? "the 1 case" : `the ${count} cases`);
  const filtered = others ? " with these filters" : "";
  const text = due === "week" ? `Nothing is due within the next 7 days${filtered}.` : `No case is past the promised day${filtered}.`;
  let next = null;
  if (due === "week" && n(stats?.past_due) > 0) next = { label: `Show ${cases(n(stats.past_due))} past the promised day`, changes: { view: "needs_action", due: "past", waiting: "", docs: "" } };
  else if (due === "past" && n(stats?.due_this_week) > 0) next = { label: `Show ${cases(n(stats.due_this_week))} due within 7 days`, changes: { view: "needs_action", due: "week", waiting: "", docs: "" } };
  else if (n(stats?.waiting_7d) > 0) next = { label: `Show ${cases(n(stats.waiting_7d))} waiting 7+ days`, changes: { view: "needs_action", due: "", waiting: 7, docs: "" } };
  return { text, next, clear: { label: `Clear “${DUE_WORDS[due]}”`, changes: { due: "" } } };
}

// What a tile's number covers, in words: "Open cases", or with filters
// "Open cases · Paid · Greece" (nadia-17). search: the tiles don't follow
// a search, and say so.
export function scopeWords(chips, search) {
  const base = search ? "All open cases, not only this search" : "Open cases";
  const parts = chips.filter((chip) => !["waiting", "docs", "due"].includes(chip.key)).map((chip) => chip.label);
  return parts.length ? `${base} · ${parts.join(" · ")}` : base;
}
